import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { SPRING_CARD } from "@/lib/motion";
import { CalendarCheck, Clock, MapPin, Navigation, Tag, Utensils } from "lucide-react";
import { cn } from "@/lib/cn";
import { useFloorState } from "@/hooks/useFloorState";
import { useActiveDiscount } from "@/hooks/useActiveDiscount";
import { useTranslation } from "@/i18n/I18nProvider";
import { CapacitySlider } from "@/components/capacity";
import { deriveCapacityLevel, discreetFill, type CapacityLevel } from "@/lib/capacityLevel";
import { formatOpeningHours, openStatusDetail } from "@/lib/openingHours";
import {
  effectiveReservationsStatus,
  type EffectiveReservationsStatus,
} from "@/lib/reservations";
import { useTaxonomy } from "@/data/TaxonomyProvider";
import { useViewerSignedIn } from "@/services/showcase";
import type { Discount, Restaurant } from "@/types/restaurant";
import type { TranslationKey } from "@/i18n/translations";
import { LEVEL_BORDER, LEVEL_DOT, LEVEL_LABEL_KEY, LEVEL_TEXT } from "./capacityStyles";

export interface RestaurantCardProps {
  restaurant: Restaurant;
  /** Whether the venue is currently open. Defaults to true. */
  isOpen?: boolean;
  /** Active discounts for this venue (from the operator's Special Events / Discounts list). */
  discounts?: Discount[];
  onSelect?: (restaurant: Restaurant) => void;
  className?: string;
  /**
   * Optional distance label (e.g. "0.4 km") shown as a badge on the hero — used
   * by the "Closest to you" strip. Absent everywhere else, so the card renders
   * exactly as before in the main feed.
   */
  distanceLabel?: string;
}

// Reservation load marker (§27): a passive line in the mono meta row.
// "neutral" inherits the row's zinc; the live levels use the capacity
// palette, since the meaning matches (room to spare / getting full / full).
const RES_LABEL_KEY: Record<EffectiveReservationsStatus, TranslationKey> = {
  neutral: "RES_MARKER_NEUTRAL",
  available: "RES_STATUS_AVAILABLE",
  limited: "RES_STATUS_LIMITED",
  full: "RES_STATUS_FULL",
};

const RES_TEXT: Record<EffectiveReservationsStatus, string> = {
  neutral: "",
  available: "text-capacity-green light:text-capacity-green-light",
  limited: "text-capacity-yellow light:text-capacity-yellow-light",
  full: "text-capacity-red light:text-capacity-red-light",
};

export function RestaurantCard({
  restaurant,
  isOpen = true,
  discounts,
  onSelect,
  className,
  distanceLabel,
}: RestaurantCardProps) {
  const { t, lang } = useTranslation();
  const reduceMotion = useReducedMotion();
  const { cuisineLabels, tagLabels } = useTaxonomy();
  // Primary cuisine (first slug) sits by the name; up to two tags read under it.
  const cuisine = cuisineLabels(restaurant.cuisine_slugs, lang)[0];
  const cardTags = tagLabels(restaurant.tag_slugs, lang);
  // Live capacity is shown only when the venue has POS view enabled AND the
  // viewer has an account — guests see every venue as if POS were off (chip,
  // body, aria-label all derive from this flag) and, because the flag also
  // gates useFloorState, a guest session never opens the floor WebSocket.
  const signedIn = useViewerSignedIn();
  const posOn = restaurant.pos_enabled === true && signedIn;
  // Discreet mode (pos_explicit === false): the availability tag + a slider
  // snapped to whole tables and floored at 25% (`discreetFill`), but no seat
  // numbers — and the green state reads "Available", so a quiet venue never
  // looks empty.
  const explicit = restaurant.pos_explicit !== false;
  // Subscribe to the shared floor-state bus so the slider tracks the same
  // table data the simulator mutates and the modal renders. Until the bus
  // hydrates (a few ms after mount) we honor the cold-start values from
  // the Restaurants row.
  const floor = useFloorState(restaurant.id, posOn);
  // Reservation load signal (§27): staff-set booked-ness, orientative only.
  // Same gating posture as live capacity — guests get the neutral marker.
  const resStatus = effectiveReservationsStatus(restaurant, signedIn);

  const totalSeats = floor?.totalSeats || restaurant.capacity_seats;
  const occupiedSeats = floor?.occupiedSeats ?? restaurant.currently_occupied_tables;
  const availableSeats = Math.max(totalSeats - occupiedSeats, 0);
  const level: CapacityLevel = floor?.level ?? deriveCapacityLevel(occupiedSeats, totalSeats);

  // Surface the operator's currently-active discount (only while open).
  const { discount: activeDiscount, countdown } = useActiveDiscount(discounts, {
    disabled: !isOpen,
  });
  // With an offer badge in the same row, the status chip drops its "Open ·"
  // prefix (the pulse dot already says open) so the offer stays readable on
  // narrow strip cards (~280px at 360px wide).
  const compactStatus = Boolean(activeDiscount && isOpen);

  // Availability tag shown on the chip + capacity body. In discreet mode the
  // green/empty state reads "Available" instead of "Wide Open".
  const levelLabel = !explicit && level === "GREEN" ? t("LEVEL_AVAILABLE") : t(LEVEL_LABEL_KEY[level]);

  // Status chip (top-left): pulse dot + "Open · until 23:30", or a dimmed
  // "Closed · opens 12:00". Pulse means open/live (as on the map pins); the
  // capacity colours appear only on the separate capacity chip, with a label.
  const hours = openStatusDetail(restaurant, isOpen);
  const statusText = isOpen
    ? hours.kind === "until" ? t("CARD_OPEN_UNTIL", { time: hours.time }) : t("CARD_OPEN")
    : hours.kind === "opens" ? t("CARD_CLOSED_OPENS", { time: hours.time }) : t("CARD_CLOSED");
  // Keying AnimatePresence by these makes each swap a true exit→enter.
  const statusKey = isOpen ? "open" : "closed";
  const capacityKey = `cap-${level}-${explicit ? "x" : "d"}`;

  return (
    <motion.button
      type="button"
      // Every venue opens its page, closed ones too (venue-detail-offers.md
      // Rule 1): the page shows its hours and when it opens.
      onClick={() => onSelect?.(restaurant)}
      whileTap={!reduceMotion ? { scale: 0.99 } : undefined}
      transition={SPRING_CARD}
      aria-label={`${restaurant.name}: ${statusText}${isOpen && posOn ? `, ${levelLabel}` : ""}`}
      className={cn(
        "group relative flex w-full flex-col overflow-hidden rounded-2xl border border-white/[0.12] light:border-black/10 bg-zinc-900/60 light:bg-white/70 text-left shadow-[0_1px_0_rgba(255,255,255,0.05)_inset] transition-[border-color,box-shadow] duration-300",
        "hover:border-white/30 light:hover:border-black/30 hover:shadow-[0_1px_0_rgba(255,255,255,0.07)_inset,0_0_0_1px_rgba(245,241,232,0.06)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-300 light:focus-visible:ring-zinc-700 focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-950",
        className,
      )}
    >
      {/* Hero image — shorter on phone (16/9) so cards aren't so tall, back to
          16/10 from sm: up. This + the body padding below are the two levers
          for "card feels too big on mobile". */}
      <div className="relative aspect-[16/9] w-full overflow-hidden bg-zinc-900 light:bg-white sm:aspect-[16/10]">
        {/* initial={false}: applying the filter instantly on mount keeps the
            image off the compositor while it decodes — animating it there
            flashed a fringe at the image's bottom clip edge on page load. */}
        <motion.img
          src={restaurant.image_url}
          alt=""
          loading="lazy"
          className="h-full w-full object-cover"
          initial={false}
          animate={{
            filter: isOpen ? "grayscale(0) brightness(1)" : "grayscale(1) brightness(0.6)",
          }}
          whileHover={isOpen && !reduceMotion ? { scale: 1.03 } : undefined}
          transition={{ duration: 0.5, ease: "easeOut" }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/90 via-zinc-950/15 to-transparent" />

        {/* Top row: status chip (left) and the live-offer badge (right). One
            flex row, so a long RO status never collides with the badge: the
            badge takes what is left and truncates. */}
        <div className="absolute inset-x-3 top-3 flex items-start justify-between gap-2">
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={statusKey}
              initial={{ opacity: 0, y: -6, scale: 0.94 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -6, scale: 0.94 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
              className={cn(
                "inline-flex shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium backdrop-blur",
                isOpen
                  ? "border-white/15 bg-zinc-950/85 text-zinc-100 light:border-black/10 light:bg-white/90 light:text-zinc-900"
                  : "border-zinc-600/50 bg-zinc-950/85 text-zinc-300 light:border-black/20 light:bg-white/90 light:text-zinc-700",
              )}
            >
              {isOpen ? (
                <span className="relative flex h-2 w-2" aria-hidden>
                  {!reduceMotion && (
                    <span className="absolute inline-flex h-full w-full rounded-full bg-pulse opacity-75 animate-pulse-soft" />
                  )}
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-pulse" />
                </span>
              ) : (
                <Clock className="h-3 w-3 text-zinc-400 light:text-zinc-600" aria-hidden />
              )}
              <span aria-hidden={compactStatus || undefined}>
                {compactStatus && hours.kind === "until" ? t("CARD_OPEN_UNTIL_SHORT", { time: hours.time }) : statusText}
              </span>
              {compactStatus && hours.kind === "until" && <span className="sr-only">{statusText}</span>}
            </motion.div>
          </AnimatePresence>

          {/* Animated discount badge: pops in on activation, out on expiry */}
          <AnimatePresence mode="popLayout">
            {activeDiscount && isOpen && (
              <motion.div
                key={`discount-${activeDiscount.id}`}
                initial={{ opacity: 0, y: -6, scale: 0.85 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{
                  opacity: 0,
                  y: -10,
                  scale: 1.18,
                  filter: "blur(2px)",
                }}
                transition={{ duration: 0.32, ease: [0.16, 1, 0.3, 1] }}
                className="inline-flex min-w-0 items-center gap-1.5 rounded-full border border-amber-400/40 bg-amber-500/15 px-2 py-1 text-2xs font-semibold text-amber-200 backdrop-blur light:border-amber-600/30 light:bg-amber-50/95 light:text-amber-900 sm:px-2.5 sm:text-xs"
              >
                <Tag className="h-3.5 w-3.5 shrink-0" aria-hidden />
                <span className="truncate">{activeDiscount.info}</span>
                {countdown && (
                  <span className="hidden shrink-0 font-mono tabular-nums text-amber-300/80 light:text-amber-800 sm:inline">· {countdown}</span>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Capacity chip (bottom-left): signed in + POS + open only. Colour
            and label always together (BRAND §3); discreet GREEN reads
            "Available". */}
        <AnimatePresence mode="wait" initial={false}>
          {isOpen && posOn && (
            <motion.div
              key={capacityKey}
              initial={{ opacity: 0, y: 6, scale: 0.94 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 6, scale: 0.94 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
              className={cn(
                "absolute bottom-3 left-3 z-10 inline-flex items-center gap-1.5 rounded-full border bg-zinc-950/85 px-2.5 py-1 text-xs font-semibold backdrop-blur light:bg-white/90",
                LEVEL_BORDER[level],
              )}
              role="status"
              aria-live="polite"
            >
              <span className={cn("inline-flex h-2 w-2 rounded-full", LEVEL_DOT[level])} aria-hidden />
              <span className={LEVEL_TEXT[level]}>{levelLabel}</span>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Distance badge: only in the "Closest to you" strip. Bottom-right over
            the darkest part of the gradient; it shares the bottom edge with the
            capacity chip (bottom-left), and both are short enough to fit side by
            side on a ~280px strip card. */}
        {distanceLabel && (
          <span className="absolute bottom-3 right-3 z-10 inline-flex items-center gap-1 rounded-full border border-white/[0.14] light:border-black/10 bg-zinc-950/85 light:bg-white/85 px-2 py-0.5 text-2xs font-medium text-zinc-100 light:text-zinc-900 backdrop-blur">
            <Navigation className="h-3 w-3 text-pulse" aria-hidden />
            {distanceLabel}
          </span>
        )}
      </div>

      {/* Body */}
      <div className="flex flex-1 flex-col gap-1.5 p-3.5 sm:p-4">
        <div className="flex items-baseline justify-between gap-3">
          <h3 className={cn("min-w-0 truncate font-display text-[1.05rem] leading-snug tracking-tight", isOpen ? "text-zinc-50 light:text-zinc-900" : "text-zinc-300 light:text-zinc-700")}>
            {restaurant.name}
          </h3>
          {cuisine && (
            // Capped + truncating so a long RO cuisine label can't overflow the
            // row or steal all the name's width.
            <span className="inline-flex max-w-[50%] shrink-0 items-center gap-1 text-xs text-zinc-400 light:text-zinc-600">
              <Utensils className="h-3 w-3 shrink-0" aria-hidden />
              <span className="min-w-0 truncate">{cuisine}</span>
            </span>
          )}
        </div>
        {cardTags.length > 0 && (
          // Single line, never wraps — so RO's longer labels can't push this to
          // a 2nd line and change the card's height. Over-long chips ellipsize;
          // the +N counter always stays visible.
          <div className="flex items-center gap-1 overflow-hidden">
            {cardTags.slice(0, 3).map((label) => (
              <span
                key={label}
                className="min-w-0 truncate rounded-md border border-white/[0.12] light:border-black/10 bg-zinc-800/60 light:bg-zinc-200/60 px-1.5 py-0.5 text-2xs text-zinc-300 light:text-zinc-700"
              >
                {label}
              </span>
            ))}
            {cardTags.length > 3 && (
              <span className="shrink-0 text-2xs text-dim light:text-zinc-600">+{cardTags.length - 3}</span>
            )}
          </div>
        )}
        {/* One line: neighborhood truncates if long, opening hours always show —
            so this row's height is the same in EN and RO. */}
        <div className="mt-1 flex items-center gap-x-3 overflow-hidden font-mono text-3xs uppercase tracking-[0.14em] text-zinc-400 light:text-zinc-600">
          {restaurant.neighborhood && (
            <span className="inline-flex min-w-0 items-center gap-1">
              <MapPin className="h-3 w-3 shrink-0" aria-hidden />
              <span className="min-w-0 truncate">{restaurant.neighborhood}</span>
            </span>
          )}
          <span className="inline-flex shrink-0 items-center gap-1">
            <Clock className="h-3 w-3 shrink-0" aria-hidden />
            {formatOpeningHours(restaurant)}
          </span>
          {resStatus !== null && (
            <span className={cn("inline-flex shrink-0 items-center gap-1", RES_TEXT[resStatus])}>
              <CalendarCheck className="h-3 w-3 shrink-0" aria-hidden />
              {t(RES_LABEL_KEY[resStatus])}
            </span>
          )}
        </div>

        {/* Live capacity body (signed in + POS + open): seats + slider, or
            the discreet number-free slider. The level label lives on the chip. */}
        <AnimatePresence initial={false} mode="wait">
          {isOpen && posOn ? (
              explicit ? (
            <motion.div
              key="slider"
              initial={{ opacity: 0, height: 0, marginTop: 0 }}
              animate={{ opacity: 1, height: "auto", marginTop: 12 }}
              exit={{ opacity: 0, height: 0, marginTop: 0 }}
              transition={reduceMotion ? { duration: 0 } : { duration: 0.35, ease: "easeOut" }}
              className="space-y-1.5 overflow-hidden"
            >
              <div className="flex items-center justify-between text-xs">
                <span className="text-zinc-400 light:text-zinc-600">
                  <motion.span
                    key={availableSeats}
                    initial={{ opacity: 0, y: -2 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2 }}
                    className={cn("inline-block font-semibold", LEVEL_TEXT[level])}
                  >
                    {availableSeats}
                  </motion.span>
                  <span className="text-dim light:text-zinc-600"> {t("CARD_SEATS_AVAILABLE", { total: totalSeats })}</span>
                </span>
              </div>
              <CapacitySlider
                occupied={occupiedSeats}
                capacity={totalSeats}
                level={level}
                ariaLabel={`${restaurant.name}: ${availableSeats} ${t("CARD_SEATS_AVAILABLE", { total: totalSeats })}`}
              />
            </motion.div>
              ) : (
                <motion.div
                  key="discreet"
                  initial={{ opacity: 0, height: 0, marginTop: 0 }}
                  animate={{ opacity: 1, height: "auto", marginTop: 12 }}
                  exit={{ opacity: 0, height: 0, marginTop: 0 }}
                  transition={reduceMotion ? { duration: 0 } : { duration: 0.35, ease: "easeOut" }}
                  className="space-y-1.5 overflow-hidden"
                >
                  {/* Rough fill: whole tables, never under a quarter, no numbers (also
                      not in the accessible name). Glides like the exact slider. */}
                  <CapacitySlider
                    occupied={discreetFill(occupiedSeats, totalSeats)}
                    capacity={totalSeats}
                    level={level}
                    ariaLabel={`${restaurant.name}: ${levelLabel}`}
                  />
                </motion.div>
              )
          ) : null}
        </AnimatePresence>
      </div>
    </motion.button>
  );
}
